import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import type { Arealplan } from '@origo/arealplan';
import { planKey } from '@origo/arealplan';
import { IconButton } from '@origo/origo-ui';
import { XIcon } from '@phosphor-icons/react';
import { formatDateLong } from '../../filter/filterUtils';
import styles from './PlanPickPopup.module.css';

interface Props {
    mapView: __esri.MapView | __esri.SceneView;
    mapPoint: __esri.Point;
    plans: Arealplan[];
    onChoose: (plan: Arealplan) => void;
    onClose: () => void;
}

/**
 * Plasser popupen sentrert over klikkpunktet, eller under det når det ikke er
 * plass over — og klem den alltid innenfor kartet med en marg.
 */
function placePopup(
    point: { x: number; y: number },
    size: { width: number; height: number },
    view: { width: number; height: number },
    gap: number,
): { left: number; top: number } {
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, Math.max(min, max)));
    const left = clamp(point.x - size.width / 2, gap, view.width - size.width - gap);
    const above = point.y - size.height - gap;
    const below = point.y + gap;
    const preferred = above >= gap || below + size.height > view.height - gap ? above : below;
    const top = clamp(preferred, gap, view.height - size.height - gap);
    return { left, top };
}

/**
 * Velger for planene et kartklikk treffer — også ved ett treff. Rendres i kart-
 * containeren, følger klikkpunktet når kartet panoreres/zoomes og holdes alltid
 * innenfor kartet.
 */
export function PlanPickPopup({ mapView, mapPoint, plans, onChoose, onClose }: Readonly<Props>) {
    const { t } = useTranslation();
    const rootRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

    // Popupen rendres skjult første gang så størrelsen kan måles før den plasseres.
    useLayoutEffect(() => {
        const update = () => {
            const el = rootRef.current;
            const screen = mapView.toScreen(mapPoint);
            if (!el || !screen) return;
            const gap = parseFloat(getComputedStyle(el).getPropertyValue('--space-03')) || 0;
            setPosition(placePopup(
                screen,
                { width: el.offsetWidth, height: el.offsetHeight },
                { width: mapView.width, height: mapView.height },
                gap,
            ));
        };
        update();
        const handles = [
            reactiveUtils.watch(() => mapView.extent, update),
            reactiveUtils.watch(() => [mapView.width, mapView.height], update),
        ];
        return () => handles.forEach((h) => h.remove());
    }, [mapView, mapPoint, plans]);

    // Gi fokus tilbake (typisk til kartet) når velgeren lukkes — men bare hvis
    // fokus ellers ville havnet på <body>, ikke når brukeren har gått videre.
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        const root = rootRef.current;
        return () => {
            const active = document.activeElement;
            if (active && active !== document.body && !root?.contains(active)) return;
            if (previous?.isConnected && previous !== document.body) previous.focus({ preventScroll: true });
            else mapView.focus();
        };
    }, [mapView]);

    // Flytt fokus inn i velgeren så den kan brukes med tastatur.
    useEffect(() => {
        rootRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    }, [plans]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        // Klikk i kartet håndteres av usePlanMapPick (nytt oppslag erstatter
        // velgeren) — her lukkes den bare ved klikk utenfor kartet.
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (mapView.container?.contains(target)) return;
            onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('pointerdown', handlePointerDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [mapView, onClose]);

    if (!mapView.container) return null;

    return createPortal(
        <div
            ref={rootRef}
            role="dialog"
            aria-label={t('admin.planMapPick.title', { count: plans.length })}
            className={styles.popup}
            style={position ?? { left: 0, top: 0, visibility: 'hidden' }}
        >
            <div className={styles.header}>
                <h2 className={styles.title}>{t('admin.planMapPick.title', { count: plans.length })}</h2>
                <IconButton
                    icon={<XIcon size={14} />}
                    label={t('admin.planMapPick.close')}
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                />
            </div>
            <ul className={styles.list}>
                {plans.map((plan) => {
                    const ikraft = formatDateLong(plan.ikraft);
                    return (
                        <li key={planKey(plan)}>
                            <button type="button" className={styles.item} onClick={() => onChoose(plan)}>
                                <span className={styles.name}>{plan.planNavn || t('admin.planMapPick.unknownPlan')}</span>
                                <span className={styles.meta}>
                                    {[t('admin.planSearch.planId', { planId: plan.planId }), ikraft && t('admin.planMapPick.ikraft', { date: ikraft })]
                                        .filter(Boolean)
                                        .join('  ·  ')}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>,
        mapView.container
    );
}
